// Scene.ts - 45度俯视院子场景系统
// 像素艺术风格 - 70/80年代中国农村

export type GameObjectType =
  | 'main_house' | 'courtyard_wall' | 'gate' | 'door'
  | 'road' | 'vegetable_patch' | 'well' | 'bamboo_chair'
  | 'chicken_coop' | 'flower_bed' | 'haystack' | 'tree'
  | 'grandma' | 'huanhuan' | 'dandan' | 'daye' | 'neighbor'
  | 'chicken' | 'cat' | 'bird'
  | 'coal_stove' | 'manure_basket' | 'hoe' | 'sickle' | 'winnowing_basket' | 'carrying_pole'
  | 'sunflower' | 'morning_glory' | 'balsam' | 'rose' | 'peony' | 'chrysanthemum'
  | 'cat_pet' | 'pigeon' | 'sparrow' | 'butterfly' | 'dragonfly'
  | 'stone_mill' | 'clothesline' | 'water_vat' | 'broom' | 'lantern' | 'kang_bed'
  | 'wicker_basket' | 'dustpan' | 'shovel' | 'axe_tool' | 'rope' | 'basket_trap'
  | 'stone_table' | 'stone_bench' | 'wooden_fence' | 'stone_paver'
  | 'fish_pond' | 'fish_bucket' | 'feeding_trough'
  | 'bamboo_pole' | 'drying_rack' | 'straw_decoration'
  | 'flower_pot' | 'bonsai' | 'rock_garden'
  | 'bird_feeder' | 'garden_gnome' | 'windmill'
  | 'swing' | 'bench' | 'footbridge'
  | 'well_bucket' | 'water_trough' | 'rain_barrel'
  | 'woodpile' | 'tool_shed' | 'storage_box'
  | 'deco_lantern' | 'paper_lantern' | 'festive_lantern'
  | 'new_year_decor' | 'spring_decor' | 'autumn_decor' | 'winter_decor'
  | 'firecracker_bundle' | 'drum' | 'bamboo_instrument'
  | 'tea_table' | 'chess_board' | 'mahjong_tile'
  | 'painting' | 'calligraphy' | 'scroll'
  | 'porcelain_vase' | 'bronze_mirror' | 'ancient_clock';

export interface GameObject {
  id: string;
  type: GameObjectType;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  hitbox: {
    type: 'rect' | 'circle';
    x: number;
    y: number;
    width?: number;
    height?: number;
    radius?: number;
  };
  baseReward: number;
  cooldown: number;
  lastClickTime: number;
  active: boolean;
  scale: number;
  rotation: number;
  onClick: () => number;
  isMovable?: boolean;
  targetX?: number;
  targetY?: number;
  speed?: number;
  animState?: { frame: number; direction: number; };
  aiState?: AIState;
  _originalX?: number;
  _originalY?: number;
  _originalW?: number;
  _originalH?: number;
}

// AI状态类型
export type GrandmaState = 'sitting_fan' | 'walking' | 'sitting_nap';
export type DayeState = 'sitting_smoke' | 'walking' | 'reading_newspaper';
export type DandanState = 'following' | 'patrolling' | 'looking_up' | 'happy';

export interface AIState {
  grandmaState?: GrandmaState;
  grandmaStateTimer?: number;
  grandmaNextStateTime?: number;
  dayeState?: DayeState;
  dayeStateTimer?: number;
  dayeNextStateTime?: number;
  dayeCoughTimer?: number;
  dandanState?: DandanState;
  dandanStateTimer?: number;
  dandanPatrolTarget?: { x: number; y: number };
  dandanHappyTimer?: number;
  catEarTimer?: number;
  chickenSoundTimer?: number;
}

export const SCENE_CONFIG = {
  width: 800,
  height: 600,
  iso: { scale: 0.8, tilt: 0.5 },
  courtyard: { x: 80, y: 150, width: 640, height: 300 },
  gate: { x: 360, y: 450, width: 80, height: 40 },
  road: { x: 280, y: 490, width: 240, height: 110 },
  // 房子放中间一点
  buildings: { mainHouse: { x: 350, y: 60, w: 220, h: 160 } },
  door: { x: 430, y: 180, w: 50, h: 50 },
  spawns: {
    grandma: { x: 450, y: 320 },
    huanhuan: { x: 280, y: 360 },
    dandan: { x: 320, y: 380 },
    daye: { x: 600, y: 300 },
    neighbor: { x: 400, y: 650 }
  }
};

export const PALETTE = {
  brickWall: '#6B8E7D', brickWallDark: '#5A7A6A', roofTile: '#4A5560', roofTileDark: '#3D4852',
  woodDoor: '#8B4513', woodFrame: '#6B4423', ground: '#C4B9A8', groundDark: '#B8A99A',
  road: '#A09585', roadDark: '#8B8378', treeLeaf: '#228B22', treeTrunk: '#8B4513',
  vegetable: '#32CD32', skin: '#F5DEB3', huanhuanDress: '#FF69B4', huanhuanHair: '#2F1810',
  dandanFur: '#DAA520', grandmaHair: '#C0C0C0', grandmaClothes: '#4169E1',
  sky: '#87CEEB', skyLight: '#E0F6FF'
};

export type FloatingTextCallback = (x: number, y: number, text: string, type?: string) => void;

export class Scene {
  private objects: GameObject[] = [];
  private width: number = 0;
  private height: number = 0;
  private time: number = 0;
  public scale: number = 1;
  public offsetX: number = 0;
  public offsetY: number = 0;
  private neighborActive: boolean = false;
  private neighborTimer: number = 0;
  private nextNeighborTime: number = 0;
  private neighborGreeted: boolean = false;
  private smokeParticles: Array<{ x: number; y: number; vx: number; vy: number; life: number; maxLife: number; scale: number; type: 'chimney' | 'coop'; }> = [];
  private onFloatingText?: FloatingTextCallback;
  private randomEventTimer: number = 0;
  private nextPigeonTime: number = 0;

  constructor() {
    this.initObjects();
    this.scheduleNextNeighbor();
  }

  setFloatingTextCallback(callback: FloatingTextCallback): void { this.onFloatingText = callback; }

  setSize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.scale = Math.min(width / SCENE_CONFIG.width, height / SCENE_CONFIG.height);
    const scaledWidth = SCENE_CONFIG.width * this.scale;
    const scaledHeight = SCENE_CONFIG.height * this.scale;
    this.offsetX = (width - scaledWidth) / 2;
    this.offsetY = (height - scaledHeight) / 2;
    this.updateObjectPositions();
  }

  private initObjects(): void {
    this.objects.push(this.createStaticObject('road', 'road', SCENE_CONFIG.road.x, SCENE_CONFIG.road.y, SCENE_CONFIG.road.width, SCENE_CONFIG.road.height, 3, 6, 1));
    this.objects.push(this.createStaticObject('courtyard_wall', 'courtyard_wall', SCENE_CONFIG.courtyard.x, SCENE_CONFIG.courtyard.y, SCENE_CONFIG.courtyard.width, SCENE_CONFIG.courtyard.height, 0, 0, 2));
    this.objects.push(this.createStaticObject('gate', 'gate', SCENE_CONFIG.gate.x, SCENE_CONFIG.gate.y, SCENE_CONFIG.gate.width, SCENE_CONFIG.gate.height, 5, 8, 4));
    this.objects.push(this.createStaticObject('main_house', 'main_house', SCENE_CONFIG.buildings.mainHouse.x, SCENE_CONFIG.buildings.mainHouse.y, SCENE_CONFIG.buildings.mainHouse.w, SCENE_CONFIG.buildings.mainHouse.h, 10, 15, 5));
    this.objects.push(this.createStaticObject('door', 'door', SCENE_CONFIG.door.x, SCENE_CONFIG.door.y, SCENE_CONFIG.door.w, SCENE_CONFIG.door.h, 5, 8, 6));
    this.objects.push(this.createStaticObject('vegetable_patch', 'vegetable_patch', 120, 220, 140, 110, 5, 8, 5));
    this.objects.push(this.createStaticObject('well', 'well', 360, 260, 60, 50, 6, 10, 6));
    this.objects.push(this.createStaticObject('chicken_coop', 'chicken_coop', 520, 240, 70, 60, 4, 8, 5));
    this.objects.push(this.createStaticObject('tree', 'tree', 60, 140, 100, 120, 5, 8, 7));
    this.objects.push(this.createStaticObject('bamboo_chair', 'bamboo_chair', 420, 310, 50, 40, 4, 6, 5));

    // 奶奶
    const grandma = this.createCharacter('grandma', 'grandma', SCENE_CONFIG.spawns.grandma.x, SCENE_CONFIG.spawns.grandma.y, 40, 50, 15, 20);
    grandma.isMovable = false;
    grandma.aiState = { grandmaState: 'sitting_fan', grandmaStateTimer: 0, grandmaNextStateTime: 10 + Math.random() * 20 };
    this.objects.push(grandma);

    // 欢欢
    const huanhuan = this.createCharacter('huanhuan', 'huanhuan', SCENE_CONFIG.spawns.huanhuan.x, SCENE_CONFIG.spawns.huanhuan.y, 35, 45, 10, 15);
    huanhuan.isMovable = true; huanhuan.speed = 150;
    this.objects.push(huanhuan);

    // 丹丹
    const dandan = this.createCharacter('dandan', 'dandan', SCENE_CONFIG.spawns.dandan.x, SCENE_CONFIG.spawns.dandan.y, 40, 35, 8, 12);
    dandan.isMovable = true; dandan.speed = 120;
    dandan.aiState = { dandanState: 'following', dandanStateTimer: 0, dandanPatrolTarget: { x: 320, y: 380 } };
    this.objects.push(dandan);

    // 大爷
    const daye = this.createCharacter('daye', 'daye', SCENE_CONFIG.spawns.daye.x, SCENE_CONFIG.spawns.daye.y, 40, 55, 8, 12);
    daye.isMovable = false;
    daye.aiState = { dayeState: 'sitting_smoke', dayeStateTimer: 0, dayeNextStateTime: 15 + Math.random() * 15, dayeCoughTimer: 5 + Math.random() * 10 };
    this.objects.push(daye);

    // 邻居
    const neighbor = this.createCharacter('neighbor', 'neighbor', SCENE_CONFIG.spawns.neighbor.x, SCENE_CONFIG.spawns.neighbor.y, 40, 50, 10, 15);
    neighbor.isMovable = true; neighbor.speed = 100; neighbor.active = false;
    this.objects.push(neighbor);

    // 鸡
    const chicken1 = this.createCharacter('chicken1', 'chicken', 160, 350, 30, 25, 3, 4);
    chicken1.isMovable = true; chicken1.speed = 40; chicken1.aiState = { chickenSoundTimer: 10 + Math.random() * 20 };
    this.objects.push(chicken1);
    const chicken2 = this.createCharacter('chicken2', 'chicken', 200, 380, 30, 25, 3, 4);
    chicken2.isMovable = true; chicken2.speed = 40; chicken2.aiState = { chickenSoundTimer: 15 + Math.random() * 25 };
    this.objects.push(chicken2);

    // 猫咪
    const catPet = this.createCharacter('cat_pet', 'cat_pet', 580, 110, 35, 25, 5, 5);
    catPet.isMovable = false; catPet.aiState = { catEarTimer: 0 };
    this.objects.push(catPet);

    this.initDecorations();
  }

  private initDecorations(): void {
    this.objects.push(this.createDecorativeObject('coal_stove', 'coal_stove', 580, 180, 50, 45, 2, 5, 6));
    this.objects.push(this.createDecorativeObject('manure_basket', 'manure_basket', 100, 320, 35, 30, 1, 3, 4));
    this.objects.push(this.createDecorativeObject('hoe', 'hoe', 130, 280, 40, 50, 1, 3, 5));
    this.objects.push(this.createDecorativeObject('sickle', 'sickle', 500, 160, 25, 25, 1, 3, 6));
    this.objects.push(this.createDecorativeObject('winnowing_basket', 'winnowing_basket', 450, 240, 45, 35, 1, 4, 5));
    this.objects.push(this.createDecorativeObject('carrying_pole', 'carrying_pole', 550, 230, 60, 10, 1, 3, 5));
    this.objects.push(this.createDecorativeObject('sunflower1', 'sunflower', 90, 200, 35, 80, 2, 8, 3));
    this.objects.push(this.createDecorativeObject('sunflower2', 'sunflower', 130, 210, 30, 70, 2, 8, 3));
    this.objects.push(this.createDecorativeObject('morning_glory1', 'morning_glory', 650, 160, 25, 40, 1, 5, 4));
    this.objects.push(this.createDecorativeObject('morning_glory2', 'morning_glory', 680, 170, 20, 35, 1, 5, 4));
    this.objects.push(this.createDecorativeObject('balsam1', 'balsam', 300, 180, 20, 30, 1, 4, 3));
    this.objects.push(this.createDecorativeObject('balsam2', 'balsam', 330, 185, 18, 28, 1, 4, 3));
    this.objects.push(this.createDecorativeObject('balsam3', 'balsam', 310, 200, 20, 30, 1, 4, 3));
    this.objects.push(this.createDecorativeObject('rose1', 'rose', 620, 280, 20, 40, 2, 6, 3));
    this.objects.push(this.createDecorativeObject('peony1', 'peony', 650, 300, 30, 35, 2, 8, 3));
    this.objects.push(this.createDecorativeObject('chrysanthemum1', 'chrysanthemum', 180, 200, 25, 35, 1, 5, 3));

    const pigeon1 = this.createCharacter('pigeon1', 'pigeon', 300, 50, 25, 20, 3, 8);
    pigeon1.isMovable = true; pigeon1.speed = 80; pigeon1.animState = { frame: 0, direction: 1 };
    this.objects.push(pigeon1);
    const sparrow1 = this.createCharacter('sparrow1', 'sparrow', 250, 280, 15, 12, 2, 10);
    sparrow1.isMovable = true; sparrow1.speed = 100;
    this.objects.push(sparrow1);
    const butterfly1 = this.createCharacter('butterfly1', 'butterfly', 200, 250, 12, 12, 1, 15);
    butterfly1.isMovable = true; butterfly1.speed = 60;
    this.objects.push(butterfly1);
  }

  private createStaticObject(id: string, type: GameObjectType, x: number, y: number, w: number, h: number, reward: number, cooldown: number, zIndex: number): GameObject {
    return {
      id, type, x, y, width: w, height: h, zIndex,
      hitbox: { type: 'rect', x: 0, y: 0, width: w, height: h },
      baseReward: reward, cooldown, lastClickTime: 0, active: true, scale: 1, rotation: 0,
      onClick: () => { const now = Date.now(); const obj = this.objects.find(o => o.id === id)!; if ((now - obj.lastClickTime) / 1000 < cooldown) return 0; obj.lastClickTime = now; return reward; }
    };
  }

  private createCharacter(id: string, type: GameObjectType, x: number, y: number, w: number, h: number, reward: number, cooldown: number): GameObject {
    return {
      id, type, x, y, width: w, height: h, zIndex: 10,
      hitbox: { type: 'circle', x: w/2, y: h/2, radius: Math.max(w, h) / 2 },
      baseReward: reward, cooldown, lastClickTime: 0, active: true, scale: 1, rotation: 0, isMovable: false, speed: 0,
      onClick: () => { const now = Date.now(); const obj = this.objects.find(o => o.id === id)!; if ((now - obj.lastClickTime) / 1000 < cooldown) return 0; obj.lastClickTime = now; return reward; }
    };
  }

  private createDecorativeObject(id: string, type: GameObjectType, x: number, y: number, w: number, h: number, reward: number, cooldown: number, zIndex: number): GameObject {
    return {
      id, type, x, y, width: w, height: h, zIndex,
      hitbox: { type: 'rect', x: 0, y: 0, width: w, height: h },
      baseReward: reward, cooldown, lastClickTime: 0, active: true, scale: 1, rotation: 0, isMovable: false, speed: 0,
      onClick: () => { const now = Date.now(); const obj = this.objects.find(o => o.id === id)!; if ((now - obj.lastClickTime) / 1000 < cooldown) return 0; obj.lastClickTime = now; obj.scale = 1.1; return reward; }
    };
  }

  private updateObjectPositions(): void {
    const scale = this.scale;
    this.objects.forEach(obj => {
      if (!obj._originalX) { obj._originalX = obj.x; obj._originalY = obj.y; obj._originalW = obj.width; obj._originalH = obj.height; }
      obj.x = obj._originalX! * scale + this.offsetX;
      obj.y = obj._originalY! * scale + this.offsetY;
      obj.width = obj._originalW! * scale;
      obj.height = obj._originalH! * scale;
    });
  }

  moveHuanhuanTo(targetX: number, targetY: number): void {
    const huanhuan = this.objects.find(o => o.type === 'huanhuan');
    if (huanhuan && huanhuan.isMovable) { huanhuan.targetX = targetX; huanhuan.targetY = targetY; }
  }

  spawnNeighbor(): void {
    const neighbor = this.objects.find(o => o.type === 'neighbor');
    if (neighbor) {
      neighbor.active = true;
      neighbor.x = (SCENE_CONFIG.gate.x + SCENE_CONFIG.gate.width / 2) * this.scale + this.offsetX;
      neighbor.y = this.height + 50;
      neighbor.targetX = (SCENE_CONFIG.gate.x + SCENE_CONFIG.gate.width / 2) * this.scale + this.offsetX;
      neighbor.targetY = (SCENE_CONFIG.gate.y + SCENE_CONFIG.gate.height) * this.scale + this.offsetY;
      this.neighborActive = true;
      this.neighborTimer = 0;
      this.neighborGreeted = false;
    }
  }

  private scheduleNextNeighbor(): void { this.nextNeighborTime = this.time + 30 + Math.random() * 90; }

  getObjectAt(x: number, y: number): GameObject | null {
    // x, y 已经是屏幕坐标，objects 的 x,y 也是屏幕坐标（经过 scale 和 offset 转换后的）
    const sorted = [...this.objects].sort((a, b) => b.zIndex - a.zIndex);
    for (const obj of sorted) {
      if (!obj.active) continue;
      const hb = obj.hitbox;
      // 计算 hitbox 的实际屏幕位置
      const hx = obj.x + hb.x * obj.scale;
      const hy = obj.y + hb.y * obj.scale;
      if (hb.type === 'rect') {
        if (x >= hx && x <= hx + (hb.width || 0) * obj.scale * this.scale &&
            y >= hy && y <= hy + (hb.height || 0) * obj.scale * this.scale) return obj;
      } else {
        const dx = x - hx;
        const dy = y - hy;
        if (dx * dx + dy * dy <= ((hb.radius || 0) * obj.scale * this.scale) ** 2) return obj;
      }
    }
    return null;
  }

  getObjects(): GameObject[] { return this.objects.filter(o => o.active); }
  getHuanhuan(): GameObject | undefined { return this.objects.find(o => o.type === 'huanhuan'); }
  getDandan(): GameObject | undefined { return this.objects.find(o => o.type === 'dandan'); }
  getNeighbor(): GameObject | undefined { return this.objects.find(o => o.type === 'neighbor'); }
  isNeighborActive(): boolean { return this.neighborActive; }

  // ===== 更新主循环 =====
  update(dt: number): void {
    this.time += dt;
    this.updateHuanhuanMovement(dt);
    this.updateGrandmaAI(dt);
    this.updateDayeAI(dt);
    this.updateDandanAI(dt);
    this.updateNeighbor(dt);
    this.updateAnimatedObjects(dt);
    this.updateRandomEvents(dt);
    this.updateSmokeEffects(dt);
    this.updateChickenSounds(dt);
    this.updateCatAnimation(dt);
    if (!this.neighborActive && this.time > this.nextNeighborTime) { if (Math.random() < 0.7) this.spawnNeighbor(); this.scheduleNextNeighbor(); }
    this.objects.forEach(obj => { if (obj.scale !== 1) { obj.scale += (1 - obj.scale) * 0.15; if (Math.abs(obj.scale - 1) < 0.001) obj.scale = 1; } });
  }

  // ===== 奶奶AI =====
  private updateGrandmaAI(dt: number): void {
    const grandma = this.objects.find(o => o.type === 'grandma');
    if (!grandma || !grandma.aiState) return;
    const ai = grandma.aiState;
    ai.grandmaStateTimer = (ai.grandmaStateTimer || 0) + dt;
    if (ai.grandmaStateTimer >= (ai.grandmaNextStateTime || 10)) {
      this.switchGrandmaState(grandma);
      ai.grandmaStateTimer = 0;
      ai.grandmaNextStateTime = 10 + Math.random() * 20;
    }
    // 移动处理
    if (grandma.isMovable && grandma.targetX && grandma.targetY) {
      const dx = grandma.targetX - (grandma.x + grandma.width / 2);
      const dy = grandma.targetY - (grandma.y + grandma.height / 2);
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 5) { const speed = 60 * dt; grandma.x += (dx / dist) * speed; grandma.y += (dy / dist) * speed; }
      else { grandma.targetX = undefined; grandma.targetY = undefined; if (ai.grandmaState !== 'walking') grandma.isMovable = false; }
    }
  }

  private switchGrandmaState(grandma: GameObject): void {
    const ai = grandma.aiState!;
    const states: GrandmaState[] = ['sitting_fan', 'walking', 'sitting_nap'];
    const currentIndex = states.indexOf(ai.grandmaState || 'sitting_fan');
    let nextIndex; do { nextIndex = Math.floor(Math.random() * states.length); } while (nextIndex === currentIndex);
    ai.grandmaState = states[nextIndex];
    if (ai.grandmaState === 'walking') {
      const targets = [
        { x: SCENE_CONFIG.spawns.grandma.x + 50, y: SCENE_CONFIG.spawns.grandma.y },
        { x: SCENE_CONFIG.spawns.grandma.x, y: SCENE_CONFIG.spawns.grandma.y + 30 },
        { x: SCENE_CONFIG.spawns.grandma.x - 30, y: SCENE_CONFIG.spawns.grandma.y }
      ];
      const target = targets[Math.floor(Math.random() * targets.length)];
      grandma.targetX = target.x * this.scale + this.offsetX;
      grandma.targetY = target.y * this.scale + this.offsetY;
      grandma.isMovable = true;
    } else {
      grandma.targetX = SCENE_CONFIG.spawns.grandma.x * this.scale + this.offsetX;
      grandma.targetY = SCENE_CONFIG.spawns.grandma.y * this.scale + this.offsetY;
      grandma.isMovable = true;
    }
  }

  // ===== 爷爷AI =====
  private updateDayeAI(dt: number): void {
    const daye = this.objects.find(o => o.type === 'daye');
    if (!daye || !daye.aiState) return;
    const ai = daye.aiState;
    ai.dayeStateTimer = (ai.dayeStateTimer || 0) + dt;
    ai.dayeCoughTimer = (ai.dayeCoughTimer || 0) - dt;
    if (ai.dayeStateTimer >= (ai.dayeNextStateTime || 15)) {
      this.switchDayeState(daye);
      ai.dayeStateTimer = 0;
      ai.dayeNextStateTime = 15 + Math.random() * 15;
    }
    if (ai.dayeState === 'sitting_smoke' && ai.dayeCoughTimer! <= 0) {
      if (this.onFloatingText && Math.random() < 0.3) this.onFloatingText(daye.x + daye.width / 2, daye.y, '咳咳...', 'info');
      ai.dayeCoughTimer = 8 + Math.random() * 12;
    }
    // 移动处理
    if (daye.isMovable && daye.targetX && daye.targetY) {
      const dx = daye.targetX - (daye.x + daye.width / 2);
      const dy = daye.targetY - (daye.y + daye.height / 2);
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 5) { const speed = 50 * dt; daye.x += (dx / dist) * speed; daye.y += (dy / dist) * speed; }
      else { daye.targetX = undefined; daye.targetY = undefined; if (ai.dayeState !== 'walking') daye.isMovable = false; }
    }
  }

  private switchDayeState(daye: GameObject): void {
    const ai = daye.aiState!;
    const states: DayeState[] = ['sitting_smoke', 'walking', 'reading_newspaper'];
    const currentIndex = states.indexOf(ai.dayeState || 'sitting_smoke');
    let nextIndex; do { nextIndex = Math.floor(Math.random() * states.length); } while (nextIndex === currentIndex);
    ai.dayeState = states[nextIndex];
    if (ai.dayeState === 'walking') {
      const targets = [{ x: SCENE_CONFIG.spawns.daye.x + 40, y: SCENE_CONFIG.spawns.daye.y }, { x: SCENE_CONFIG.spawns.daye.x, y: SCENE_CONFIG.spawns.daye.y + 25 }];
      const target = targets[Math.floor(Math.random() * targets.length)];
      daye.targetX = target.x * this.scale + this.offsetX;
      daye.targetY = target.y * this.scale + this.offsetY;
      daye.isMovable = true;
    } else {
      daye.targetX = SCENE_CONFIG.spawns.daye.x * this.scale + this.offsetX;
      daye.targetY = SCENE_CONFIG.spawns.daye.y * this.scale + this.offsetY;
      daye.isMovable = true;
    }
  }

  // ===== 丹丹AI =====
  private updateDandanAI(dt: number): void {
    const huanhuan = this.objects.find(o => o.type === 'huanhuan');
    const dandan = this.objects.find(o => o.type === 'dandan');
    if (!huanhuan || !dandan || !dandan.aiState) return;
    const ai = dandan.aiState;
    ai.dandanStateTimer = (ai.dandanStateTimer || 0) + dt;
    const hx = huanhuan.x + huanhuan.width / 2;
    const hy = huanhuan.y + huanhuan.height / 2;
    const dx = hx - (dandan.x + dandan.width / 2);
    const dy = hy - (dandan.y + dandan.height / 2);
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (huanhuan.targetX && dist > 60) {
      ai.dandanState = 'following';
      const speed = (dandan.speed || 120) * dt * 0.8;
      if (dist > 0) { dandan.x += (dx / dist) * speed; dandan.y += (dy / dist) * speed; }
    } else if (ai.dandanState === 'following' && dist <= 60) {
      ai.dandanState = 'patrolling';
      ai.dandanPatrolTarget = this.getRandomPatrolPoint();
    } else if (ai.dandanState === 'patrolling') {
      const target = ai.dandanPatrolTarget!;
      const pdx = target.x - (dandan.x + dandan.width / 2);
      const pdy = target.y - (dandan.y + dandan.height / 2);
      const pdist = Math.sqrt(pdx * pdx + pdy * pdy);
      if (pdist > 10) { const speed = (dandan.speed || 120) * dt * 0.5; dandan.x += (pdx / pdist) * speed; dandan.y += (pdy / pdist) * speed; }
      else { ai.dandanPatrolTarget = this.getRandomPatrolPoint(); }
      if (ai.dandanStateTimer! > 5) { ai.dandanState = 'following'; ai.dandanStateTimer = 0; }
    }
    if (ai.dandanState === 'happy') { ai.dandanHappyTimer = (ai.dandanHappyTimer || 1) - dt; if (ai.dandanHappyTimer <= 0) ai.dandanState = 'following'; }
  }

  private getRandomPatrolPoint(): { x: number; y: number } {
    return [
      { x: 250 * this.scale + this.offsetX, y: 320 * this.scale + this.offsetY },
      { x: 350 * this.scale + this.offsetX, y: 300 * this.scale + this.offsetY },
      { x: 400 * this.scale + this.offsetX, y: 350 * this.scale + this.offsetY },
      { x: 300 * this.scale + this.offsetX, y: 400 * this.scale + this.offsetY }
    ][Math.floor(Math.random() * 4)];
  }

  makeDandanHappy(): void {
    const dandan = this.objects.find(o => o.type === 'dandan');
    if (dandan && dandan.aiState) { dandan.aiState.dandanState = 'happy'; dandan.aiState.dandanHappyTimer = 3; }
  }

  dandanLookUp(): void {
    const dandan = this.objects.find(o => o.type === 'dandan');
    if (dandan && dandan.aiState && dandan.aiState.dandanState !== 'happy') {
      const prevState = dandan.aiState.dandanState;
      dandan.aiState.dandanState = 'looking_up';
      setTimeout(() => { if (dandan.aiState) dandan.aiState.dandanState = prevState; }, 2000);
    }
  }

  // ===== 烟雾效果 =====
  private updateSmokeEffects(dt: number): void {
    if (Math.floor(this.time * 10) % 150 === 0) {
      this.spawnSmoke('chimney',
        (SCENE_CONFIG.buildings.mainHouse.x + SCENE_CONFIG.buildings.mainHouse.w / 2) * this.scale + this.offsetX,
        (SCENE_CONFIG.buildings.mainHouse.y + 10) * this.scale + this.offsetY);
    }
    if (Math.floor(this.time * 10) % 300 === 0 && Math.random() < 0.5) {
      this.spawnSmoke('coop', (520 + 35) * this.scale + this.offsetX, (240 + 20) * this.scale + this.offsetY);
    }
    this.smokeParticles = this.smokeParticles.filter(p => {
      p.x += p.vx * dt; p.y += p.vy * dt; p.life -= dt; p.scale += dt * 0.2;
      return p.life > 0;
    });
  }

  private spawnSmoke(type: 'chimney' | 'coop', x: number, y: number): void {
    for (let i = 0; i < 5; i++) {
      this.smokeParticles.push({
        x: x + (Math.random() - 0.5) * 10, y: y - Math.random() * 5,
        vx: (Math.random() - 0.5) * 15, vy: -20 - Math.random() * 15,
        life: 2 + Math.random() * 2, maxLife: 4, scale: 0.5 + Math.random() * 0.5, type
      });
    }
  }

  // ===== 鸡叫 =====
  private updateChickenSounds(dt: number): void {
    this.objects.filter(o => o.type === 'chicken').forEach(chicken => {
      if (!chicken.aiState) chicken.aiState = {};
      chicken.aiState.chickenSoundTimer = (chicken.aiState.chickenSoundTimer || 10) - dt;
      if (chicken.aiState.chickenSoundTimer <= 0) {
        if (this.onFloatingText && Math.random() < 0.4) this.onFloatingText(chicken.x + chicken.width / 2, chicken.y - 20, '咯咯~', 'info');
        chicken.aiState.chickenSoundTimer = 20 + Math.random() * 30;
      }
    });
  }

  // ===== 猫咪耳朵动画 =====
  private updateCatAnimation(dt: number): void {
    const cat = this.objects.find(o => o.type === 'cat_pet');
    if (cat && cat.aiState) cat.aiState.catEarTimer = (cat.aiState.catEarTimer || 0) + dt;
  }

  // ===== 其他更新 =====
  private updateAnimatedObjects(dt: number): void {
    this.objects.forEach(obj => {
      if (!obj.active || !obj.animState || !obj.speed) return;
      if (obj.type === 'pigeon') { const flySpeed = obj.speed * dt; obj.x += flySpeed; if (obj.x > this.width + 100) obj.active = false; }
    });
  }

  private updateRandomEvents(dt: number): void {
    this.randomEventTimer += dt;
    if (this.time > this.nextPigeonTime) { if (Math.random() < 0.3) this.spawnPigeon(); this.nextPigeonTime = this.time + 30 + Math.random() * 60; }
  }

  spawnPigeon(): void {
    const pigeon = this.objects.find(o => o.type === 'pigeon');
    if (pigeon) { pigeon.active = true; pigeon.x = -50; pigeon.y = 50 + Math.random() * 100; }
  }

  private updateHuanhuanMovement(dt: number): void {
    const huanhuan = this.objects.find(o => o.type === 'huanhuan');
    if (!huanhuan || !huanhuan.targetX || !huanhuan.targetY) return;
    const dx = huanhuan.targetX - (huanhuan.x + huanhuan.width / 2);
    const dy = huanhuan.targetY - (huanhuan.y + huanhuan.height / 2);
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > 5) { const speed = (huanhuan.speed || 150) * dt; huanhuan.x += (dx / dist) * speed; huanhuan.y += (dy / dist) * speed; }
    else { huanhuan.targetX = undefined; huanhuan.targetY = undefined; }
  }

  private updateNeighbor(dt: number): void {
    const neighbor = this.objects.find(o => o.type === 'neighbor');
    const grandma = this.objects.find(o => o.type === 'grandma');
    if (!neighbor || !neighbor.active) return;
    if (neighbor.targetX && neighbor.targetY) {
      const nx = neighbor.x + neighbor.width / 2;
      const ny = neighbor.y + neighbor.height / 2;
      const dx = neighbor.targetX - nx;
      const dy = neighbor.targetY - ny;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 5) { const speed = (neighbor.speed || 100) * dt; neighbor.x += (dx / dist) * speed; neighbor.y += (dy / dist) * speed; }
      else {
        this.neighborTimer += dt;
        if (!this.neighborGreeted && grandma && dist < 50) {
          this.neighborGreeted = true;
          if (this.onFloatingText) this.onFloatingText(grandma.x + grandma.width / 2, grandma.y - 30, '来啦！快进来坐~', 'info');
          this.makeDandanHappy();
        }
        if (this.neighborTimer > 5 && this.neighborTimer <= 5.1) { neighbor.targetX = nx; neighbor.targetY = this.height + 100; }
        else if (neighbor.y > this.height + 40) { neighbor.active = false; this.neighborActive = false; this.neighborTimer = 0; this.neighborGreeted = false; }
      }
    }
  }

  // ===== 渲染 =====
  render(ctx: CanvasRenderingContext2D): void {
    ctx.clearRect(0, 0, this.width, this.height);
    this.renderBackground(ctx);
    const sorted = [...this.objects].filter(o => o.active).sort((a, b) => a.zIndex - b.zIndex);
    sorted.forEach(obj => this.renderObject(ctx, obj));
    this.renderSmokeEffects(ctx);
    this.renderTargetMarker(ctx);
  }

  private renderSmokeEffects(ctx: CanvasRenderingContext2D): void {
    this.smokeParticles.forEach(p => {
      const alpha = p.life / p.maxLife * 0.4;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = p.type === 'coop' ? 'rgba(200, 200, 200, 0.8)' : 'rgba(150, 150, 150, 0.8)';
      ctx.beginPath();
      ctx.arc(p.x, p.y, 8 * p.scale, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });
  }

  private renderBackground(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.translate(this.offsetX, this.offsetY);
    const skyGrad = ctx.createLinearGradient(0, 0, 0, SCENE_CONFIG.height * this.scale * 0.55);
    skyGrad.addColorStop(0, '#4A90E2');
    skyGrad.addColorStop(0.5, PALETTE.sky);
    skyGrad.addColorStop(1, PALETTE.skyLight);
    ctx.fillStyle = skyGrad;
    ctx.fillRect(0, 0, SCENE_CONFIG.width * this.scale, SCENE_CONFIG.height * this.scale * 0.55);
    ctx.fillStyle = '#7A9E7E';
    ctx.beginPath();
    ctx.moveTo(0, SCENE_CONFIG.height * this.scale * 0.45);
    for (let i = 0; i <= SCENE_CONFIG.width * this.scale; i += 40 * this.scale) {
      const h = SCENE_CONFIG.height * this.scale * 0.45 - Math.sin(i * 0.008 / this.scale) * 40 * this.scale - Math.cos(i * 0.015 / this.scale) * 20 * this.scale - 15 * this.scale;
      ctx.lineTo(i, h);
    }
    ctx.lineTo(SCENE_CONFIG.width * this.scale, SCENE_CONFIG.height * this.scale * 0.45);
    ctx.fill();
    ctx.fillStyle = '#8FBC8F';
    ctx.beginPath();
    ctx.moveTo(0, SCENE_CONFIG.height * this.scale * 0.48);
    for (let i = 0; i <= SCENE_CONFIG.width * this.scale; i += 60 * this.scale) {
      const h = SCENE_CONFIG.height * this.scale * 0.48 - Math.sin(i * 0.012 / this.scale + 1) * 25 * this.scale - 10 * this.scale;
      ctx.lineTo(i, h);
    }
    ctx.lineTo(SCENE_CONFIG.width * this.scale, SCENE_CONFIG.height * this.scale * 0.48);
    ctx.fill();
    ctx.restore();
  }

  private renderObject(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    ctx.save();
    const cx = obj.x + obj.width / 2;
    const cy = obj.y + obj.height / 2;
    ctx.translate(cx, cy);
    ctx.scale(obj.scale, obj.scale);
    ctx.translate(-cx, -cy);
    switch (obj.type) {
      case 'main_house': this.renderMainHouse(ctx, obj); break;
      case 'door': this.renderDoor(ctx, obj); break;
      case 'courtyard_wall': this.renderCourtyardWall(ctx, obj); break;
      case 'gate': this.renderGate(ctx, obj); break;
      case 'road': this.renderRoad(ctx, obj); break;
      case 'well': this.renderWell(ctx, obj); break;
      case 'vegetable_patch': this.renderVegetablePatch(ctx, obj); break;
      case 'chicken_coop': this.renderChickenCoop(ctx, obj); break;
      case 'bamboo_chair': this.renderBambooChair(ctx, obj); break;
      case 'tree': this.renderTree(ctx, obj); break;
      case 'grandma': this.renderGrandma(ctx, obj); break;
      case 'huanhuan': this.renderHuanhuan(ctx, obj); break;
      case 'dandan': this.renderDandan(ctx, obj); break;
      case 'daye': this.renderDaye(ctx, obj); break;
      case 'neighbor': this.renderNeighbor(ctx, obj); break;
      case 'chicken': this.renderChicken(ctx, obj); break;
      case 'cat_pet': this.renderCatPet(ctx, obj); break;
      case 'pigeon': this.renderPigeon(ctx, obj); break;
      case 'sparrow': this.renderSparrow(ctx, obj); break;
      case 'butterfly': this.renderButterfly(ctx, obj); break;
      case 'dragonfly': this.renderDragonfly(ctx, obj); break;
      case 'coal_stove': this.renderCoalStove(ctx, obj); break;
      case 'manure_basket': this.renderManureBasket(ctx, obj); break;
      case 'hoe': this.renderHoe(ctx, obj); break;
      case 'sickle': this.renderSickle(ctx, obj); break;
      case 'winnowing_basket': this.renderWinnowingBasket(ctx, obj); break;
      case 'carrying_pole': this.renderCarryingPole(ctx, obj); break;
      case 'sunflower': this.renderSunflower(ctx, obj); break;
      case 'morning_glory': this.renderMorningGlory(ctx, obj); break;
      case 'balsam': this.renderBalsam(ctx, obj); break;
      case 'rose': this.renderRose(ctx, obj); break;
      case 'peony': this.renderPeony(ctx, obj); break;
      case 'chrysanthemum': this.renderChrysanthemum(ctx, obj); break;
    }
    ctx.restore();
  }

  // ===== 具体渲染方法 =====
  private renderMainHouse(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x, y = obj.y, w = obj.width, h = obj.height;
    ctx.fillStyle = 'rgba(0,0,0,0.2)'; ctx.fillRect(x + 8, y + h - 10, w, 15);
    ctx.fillStyle = PALETTE.brickWall; ctx.fillRect(x + 10, y + h * 0.35, w - 20, h * 0.65);
    ctx.strokeStyle = PALETTE.brickWallDark; ctx.lineWidth = 1;
    const brickW = w / 12, brickH = h * 0.65 / 8;
    for (let row = 0; row < 8; row++) { const offset = (row % 2) * (brickW / 2); for (let col = -1; col < 13; col++) ctx.strokeRect(x + 10 + col * brickW + offset, y + h * 0.35 + row * brickH, brickW, brickH); }
    ctx.fillStyle = PALETTE.roofTile; ctx.beginPath(); ctx.moveTo(x, y + h * 0.35); ctx.lineTo(x + w / 2, y); ctx.lineTo(x + w, y + h * 0.35); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = PALETTE.roofTileDark; ctx.lineWidth = 1;
    for (let i = 0; i < 10; i++) { const wy = y + 10 + i * (h * 0.35 - 10) / 10; const wx1 = x + (wy - y) * (w / 2) / (h * 0.35); const wx2 = x + w - (wy - y) * (w / 2) / (h * 0.35); ctx.beginPath(); ctx.moveTo(wx1, wy); ctx.lineTo(wx2, wy); ctx.stroke(); }
    ctx.fillStyle = PALETTE.woodDoor; ctx.fillRect(x + w / 2 - w * 0.12, y + h * 0.5, w * 0.24, h * 0.5);
    ctx.strokeStyle = PALETTE.woodFrame; ctx.lineWidth = 3; ctx.strokeRect(x + w / 2 - w * 0.12, y + h * 0.5, w * 0.24, h * 0.5);
    ctx.fillStyle = '#8B7355'; ctx.beginPath(); ctx.arc(x + w / 2 + w * 0.06, y + h * 0.65, 4, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#87CEEB'; ctx.fillRect(x + w * 0.15, y + h * 0.45, w * 0.15, h * 0.18);
    ctx.strokeStyle = PALETTE.woodFrame; ctx.lineWidth = 3; ctx.strokeRect(x + w * 0.15, y + h * 0.45, w * 0.15, h * 0.18);
    ctx.beginPath(); ctx.moveTo(x + w * 0.225, y + h * 0.45); ctx.lineTo(x + w * 0.225, y + h * 0.63); ctx.moveTo(x + w * 0.15, y + h * 0.54); ctx.lineTo(x + w * 0.30, y + h * 0.54); ctx.stroke();
  }

  private renderDoor(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x, y = obj.y, w = obj.width, h = obj.height;
    // 门框
    ctx.fillStyle = PALETTE.woodFrame;
    ctx.fillRect(x - 5, y - 5, w + 10, h + 10);
    // 门板
    ctx.fillStyle = PALETTE.woodDoor;
    ctx.fillRect(x, y, w, h);
    // 门板纹理
    ctx.strokeStyle = PALETTE.woodFrame;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x + w * 0.5, y + 5);
    ctx.lineTo(x + w * 0.5, y + h - 5);
    ctx.stroke();
    // 门环
    ctx.fillStyle = '#FFD700';
    ctx.beginPath();
    ctx.arc(x + w * 0.75, y + h * 0.5, 5, 0, Math.PI * 2);
    ctx.fill();
    // 门牌
    ctx.fillStyle = 'rgba(139, 195, 74, 0.8)';
    ctx.beginPath();
    ctx.roundRect(x + 5, y - 20, w - 10, 18, 4);
    ctx.fill();
    ctx.fillStyle = '#FFF';
    ctx.font = 'bold 10px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('🏠 进屋', x + w / 2, y - 8);
    // 高亮边框（可交互提示）
    ctx.strokeStyle = 'rgba(139, 195, 74, 0.6)';
    ctx.lineWidth = 3;
    ctx.strokeRect(x - 3, y - 3, w + 6, h + 6);
  }

  private renderCourtyardWall(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x, y = obj.y, w = obj.width, h = obj.height;
    const groundGrad = ctx.createLinearGradient(x, y, x, y + h);
    groundGrad.addColorStop(0, '#D4C4A8'); groundGrad.addColorStop(0.5, PALETTE.ground); groundGrad.addColorStop(1, PALETTE.groundDark);
    ctx.fillStyle = groundGrad; ctx.fillRect(x, y, w, h);
    ctx.fillStyle = 'rgba(0,0,0,0.05)'; for (let i = 0; i < 30; i++) { const tx = x + (i * 73) % w; const ty = y + (i * 37) % h; ctx.beginPath(); ctx.ellipse(tx, ty, 3 + (i % 5), 2 + (i % 3), 0, 0, Math.PI * 2); ctx.fill(); }
    ctx.fillStyle = PALETTE.brickWall; ctx.fillRect(x, y, w, 35); ctx.fillRect(x, y, 18, h); ctx.fillRect(x + w - 18, y, 18, h);
    ctx.strokeStyle = PALETTE.brickWallDark; ctx.lineWidth = 1;
    for (let row = 0; row < 4; row++) { const offset = (row % 2) * 15; for (let col = 0; col < w / 30 + 2; col++) ctx.strokeRect(x + col * 30 + offset, y + row * 9, 30, 9); }
    for (let row = 0; row < h / 12; row++) { const offset = (row % 2) * 9; ctx.strokeRect(x + offset, y + row * 12, 9, 12); ctx.strokeRect(x + w - 18 + offset, y + row * 12, 9, 12); }
    ctx.fillStyle = PALETTE.roofTile;
    for (let i = 0; i < w / 18; i++) { ctx.beginPath(); ctx.arc(x + i * 18 + 9, y, 9, 0, Math.PI, true); ctx.fill(); }
    for (let i = 0; i < h / 18; i++) { ctx.beginPath(); ctx.arc(x, y + i * 18 + 9, 9, Math.PI * 0.5, Math.PI * 1.5); ctx.fill(); ctx.beginPath(); ctx.arc(x + w, y + i * 18 + 9, 9, Math.PI * 1.5, Math.PI * 0.5); ctx.fill(); }
    ctx.fillStyle = 'rgba(0,0,0,0.1)'; ctx.fillRect(x + 18, y + 35, w - 36, 8); ctx.fillRect(x + 18, y + 35, 8, h - 35);
  }

  private renderGate(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x, y = obj.y, w = obj.width, h = obj.height;
    ctx.fillStyle = PALETTE.groundDark; ctx.fillRect(x + 8, y, w - 16, h);
    ctx.fillStyle = PALETTE.woodFrame; ctx.fillRect(x, y - 5, 12, h + 10); ctx.fillRect(x + w - 12, y - 5, 12, h + 10);
    ctx.fillStyle = PALETTE.brickWallDark; ctx.fillRect(x + 8, y, w - 16, 8);
    ctx.fillStyle = PALETTE.roofTile; ctx.beginPath(); ctx.moveTo(x - 5, y - 5); ctx.lineTo(x + w / 2, y - 30); ctx.lineTo(x + w + 5, y - 5); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = PALETTE.roofTileDark; ctx.lineWidth = 1;
    for (let i = 0; i < 5; i++) { const wy = y - 25 + i * 5; const wx1 = x + (wy - (y - 30)) * ((w / 2 + 5) / 25); const wx2 = x + w - (wy - (y - 30)) * ((w / 2 + 5) / 25); ctx.beginPath(); ctx.moveTo(wx1, wy); ctx.lineTo(wx2, wy); ctx.stroke(); }
    ctx.fillStyle = PALETTE.woodDoor; ctx.fillRect(x + 3, y + 5, (w - 16) / 2 - 2, h - 10); ctx.fillRect(x + 8 + (w - 16) / 2, y + 5, (w - 16) / 2 - 2, h - 10);
    ctx.fillStyle = '#8B7355'; ctx.beginPath(); ctx.arc(x + (w - 16) / 2 - 5, y + h / 2, 4, 0, Math.PI * 2); ctx.arc(x + 8 + (w - 16) / 2 + 5, y + h / 2, 4, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = 'rgba(0,0,0,0.15)'; ctx.fillRect(x + 8, y + h - 5, w - 16, 10);
  }

  private renderRoad(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x, y = obj.y, w = obj.width, h = obj.height;
    const roadGrad = ctx.createLinearGradient(x, y, x, y + h); roadGrad.addColorStop(0, PALETTE.road); roadGrad.addColorStop(1, '#958B7A'); ctx.fillStyle = roadGrad; ctx.fillRect(x, y, w, h);
    ctx.fillStyle = 'rgba(0,0,0,0.08)'; ctx.fillRect(x + w * 0.3, y, 8, h); ctx.fillRect(x + w * 0.7, y, 8, h);
    for (let i = 0; i < 15; i++) { const rx = x + 20 + (i * 47) % (w - 40); const ry = y + 15 + (i * 31) % (h - 30); const size = 3 + (i % 4); ctx.beginPath(); ctx.ellipse(rx, ry, size, size * 0.6, 0, 0, Math.PI * 2); ctx.fill(); }
    ctx.fillStyle = '#7C9A5E'; ctx.fillRect(x - 20, y, 20, h); ctx.fillRect(x + w, y, 20, h);
    ctx.strokeStyle = '#6B8E4E'; ctx.lineWidth = 2;
    for (let i = 0; i < h / 15; i++) { const gy = y + i * 15 + 5; ctx.beginPath(); ctx.moveTo(x - 15, gy); ctx.lineTo(x - 10, gy - 5); ctx.stroke(); ctx.beginPath(); ctx.moveTo(x + w + 15, gy); ctx.lineTo(x + w + 10, gy - 5); ctx.stroke(); }
    ctx.fillStyle = '#8B8378'; ctx.fillRect(x + w * 0.35, y, w * 0.3, 10);
  }

  private renderWell(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x + obj.width / 2, y = obj.y + obj.height / 2;
    ctx.fillStyle = '#9B9B93'; ctx.beginPath(); ctx.ellipse(x, y, obj.width / 2, obj.height / 3, 0, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#ABABA3'; ctx.lineWidth = 2; ctx.beginPath(); ctx.ellipse(x, y, obj.width / 2, obj.height / 3, 0, 0, Math.PI, true); ctx.stroke();
    ctx.fillStyle = '#1F1F1F'; ctx.beginPath(); ctx.ellipse(x, y - 2, obj.width / 3, obj.height / 5, 0, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#7B7B73'; ctx.lineWidth = 3; ctx.beginPath(); ctx.ellipse(x, y - 2, obj.width / 3, obj.height / 5, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = PALETTE.woodFrame; ctx.fillRect(x - 28, y - obj.height / 2 - 15, 5, 35); ctx.fillRect(x + 23, y - obj.height / 2 - 15, 5, 35); ctx.fillRect(x - 28, y - obj.height / 2 - 15, 56, 5);
    ctx.strokeStyle = '#8B7355'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(x, y - obj.height / 2 - 15); ctx.lineTo(x, y - 5); ctx.stroke();
  }

  private renderVegetablePatch(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x, y = obj.y, w = obj.width, h = obj.height;
    ctx.fillStyle = '#8B4513'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 5); ctx.fill();
    const vegColors = ['#228B22', '#32CD32', '#90EE90'];
    for (let row = 0; row < 3; row++) { for (let col = 0; col < 4; col++) { const vx = x + 15 + col * 25; const vy = y + 15 + row * 30; ctx.fillStyle = vegColors[(row + col) % 3]; ctx.beginPath(); ctx.ellipse(vx, vy, 8, 12, 0, 0, Math.PI * 2); ctx.fill(); } }
  }

  private renderBambooChair(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x, y = obj.y + obj.height;
    ctx.fillStyle = '#D2691E'; ctx.fillRect(x + 5, y - obj.height * 0.8, 4, obj.height * 0.8); ctx.fillRect(x + obj.width - 9, y - obj.height * 0.8, 4, obj.height * 0.8); ctx.fillRect(x + 3, y - obj.height * 0.8, obj.width - 6, 4);
    ctx.fillStyle = '#CD853F'; ctx.fillRect(x, y - 8, obj.width, 8);
    ctx.fillStyle = '#D2691E'; ctx.fillRect(x + 3, y - 8, 4, obj.height); ctx.fillRect(x + obj.width - 7, y - 8, 4, obj.height);
  }

  private renderTree(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x + obj.width / 2, y = obj.y + obj.height;
    const windSway = Math.sin(this.time * 0.8) * 3;
    ctx.fillStyle = PALETTE.treeTrunk; ctx.fillRect(x - 12, y - 60, 24, 60);
    ctx.fillStyle = PALETTE.treeLeaf;
    const leaves = [{ dx: 0, dy: -70, r: 35 }, { dx: -25, dy: -55, r: 28 }, { dx: 25, dy: -55, r: 28 }, { dx: -15, dy: -85, r: 25 }, { dx: 15, dy: -85, r: 25 }];
    leaves.forEach((leaf, index) => { const sway = windSway * (1 + index * 0.2); ctx.beginPath(); ctx.arc(x + leaf.dx + sway, y + leaf.dy, leaf.r, 0, Math.PI * 2); ctx.fill(); });
  }

  private renderGrandma(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x + obj.width / 2, y = obj.y + obj.height;
    const ai = obj.aiState, state = ai?.grandmaState || 'sitting_fan';
    const fanSway = state === 'sitting_fan' ? Math.sin(this.time * 3) * 5 : 0;
    const napOffset = state === 'sitting_nap' ? 5 : 0;
    const walkBounce = state === 'walking' && obj.targetX ? Math.abs(Math.sin(this.time * 6)) * 3 : 0;
    ctx.fillStyle = PALETTE.grandmaClothes; ctx.beginPath(); ctx.ellipse(x, y - 30 + walkBounce, 18, 25, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = PALETTE.skin; ctx.beginPath(); ctx.arc(x, y - 55 + walkBounce - napOffset, 14, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = PALETTE.grandmaHair; ctx.beginPath(); ctx.arc(x, y - 60 + walkBounce - napOffset, 15, Math.PI, 0); ctx.fill();
    if (state === 'sitting_nap') { ctx.strokeStyle = '#333'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(x - 6, y - 55 + walkBounce - napOffset); ctx.lineTo(x - 2, y - 55 + walkBounce - napOffset); ctx.moveTo(x + 2, y - 55 + walkBounce - napOffset); ctx.lineTo(x + 6, y - 55 + walkBounce - napOffset); ctx.stroke(); }
    else { ctx.fillStyle = '#333'; ctx.beginPath(); ctx.arc(x - 4, y - 55 + walkBounce - napOffset, 2, 0, Math.PI * 2); ctx.arc(x + 4, y - 55 + walkBounce - napOffset, 2, 0, Math.PI * 2); ctx.fill(); }
    ctx.strokeStyle = '#333'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(x, y - 48 + walkBounce - napOffset, 3, 0, Math.PI); ctx.stroke();
    if (state === 'sitting_fan') { ctx.save(); ctx.translate(x - 15, y - 35 + walkBounce); ctx.rotate((fanSway * Math.PI) / 180); ctx.fillStyle = '#D2B48C'; ctx.beginPath(); ctx.moveTo(0, 0); ctx.quadraticCurveTo(-10, -5, -8, -15); ctx.quadraticCurveTo(0, -20, 8, -15); ctx.quadraticCurveTo(10, -5, 0, 0); ctx.fill(); ctx.restore(); }
  }

  private renderHuanhuan(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x + obj.width / 2, y = obj.y + obj.height;
    const bounce = obj.targetX ? Math.abs(Math.sin(this.time * 8)) * 3 : 0;
    ctx.fillStyle = PALETTE.skin; ctx.fillRect(x - 6, y - 15 + bounce, 4, 12); ctx.fillRect(x + 2, y - 15 + bounce, 4, 12);
    ctx.fillStyle = '#8B4513'; ctx.fillRect(x - 7, y - 5 + bounce, 6, 4); ctx.fillRect(x + 1, y - 5 + bounce, 6, 4);
    ctx.fillStyle = PALETTE.huanhuanDress; ctx.beginPath(); ctx.moveTo(x - 15, y - 15 + bounce); ctx.lineTo(x + 15, y - 15 + bounce); ctx.lineTo(x + 12, y - 40 + bounce); ctx.lineTo(x - 12, y - 40 + bounce); ctx.closePath(); ctx.fill();
    ctx.fillStyle = '#FFB6C1'; ctx.fillRect(x - 10, y - 52 + bounce, 20, 15);
    ctx.fillStyle = PALETTE.skin; ctx.fillRect(x - 14, y - 48 + bounce, 4, 12); ctx.fillRect(x + 10, y - 48 + bounce, 4, 12);
    ctx.fillStyle = PALETTE.skin; ctx.beginPath(); ctx.arc(x, y - 62 + bounce, 13, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = PALETTE.huanhuanHair; ctx.beginPath(); ctx.arc(x, y - 65, 14, Math.PI, 0); ctx.fill(); ctx.beginPath(); ctx.ellipse(x - 12, y - 55 + bounce, 5, 10, -0.3, 0, Math.PI * 2); ctx.fill(); ctx.beginPath(); ctx.ellipse(x + 12, y - 55 + bounce, 5, 10, 0.3, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#FF0000'; ctx.beginPath(); ctx.arc(x - 10, y - 48 + bounce, 2, 0, Math.PI * 2); ctx.arc(x + 10, y - 48 + bounce, 2, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#333'; ctx.beginPath(); ctx.arc(x - 4, y - 62 + bounce, 2, 0, Math.PI * 2); ctx.arc(x + 4, y - 62 + bounce, 2, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#FF6B6B'; ctx.beginPath(); ctx.arc(x, y - 55 + bounce, 3, 0, Math.PI); ctx.fill();
  }

  private renderDandan(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x + obj.width / 2, y = obj.y + obj.height;
    const ai = obj.aiState, state = ai?.dandanState || 'following';
    const tailSpeed = state === 'happy' ? 15 : 6;
    const tailAmp = state === 'happy' ? 10 : 5;
    const tailWag = Math.sin(this.time * tailSpeed) * tailAmp;
    const headOffset = state === 'looking_up' ? -8 : 0;
    ctx.fillStyle = PALETTE.dandanFur; ctx.beginPath(); ctx.ellipse(x, y - 12, 18, 12, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#B8860B'; ctx.fillRect(x - 15, y - 8, 5, 10); ctx.fillRect(x - 5, y - 8, 5, 10); ctx.fillRect(x + 2, y - 8, 5, 10); ctx.fillRect(x + 10, y - 8, 5, 10);
    ctx.fillStyle = PALETTE.dandanFur; ctx.beginPath(); ctx.roundRect(x + 8, y - 30 + headOffset, 18, 18, 6); ctx.fill();
    ctx.fillStyle = '#B8860B'; const earAngle = state === 'looking_up' ? -0.5 : -0.3; ctx.beginPath(); ctx.ellipse(x + 5, y - 32 + headOffset, 5, 8, earAngle, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#333'; ctx.beginPath(); ctx.arc(x + 18, y - 22 + headOffset, 2.5, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#000'; ctx.beginPath(); ctx.ellipse(x + 26, y - 18 + headOffset, 3, 2, 0, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = PALETTE.dandanFur; ctx.lineWidth = 4; ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(x - 15, y - 15); ctx.quadraticCurveTo(x - 28, y - 20 + tailWag, x - 32, y - 25 + tailWag * 0.5); ctx.stroke();
    if (state === 'happy') { ctx.fillStyle = '#FF69B4'; ctx.beginPath(); ctx.moveTo(x + 25, y - 35 + headOffset); ctx.bezierCurveTo(x + 22, y - 38 + headOffset, x + 22, y - 42 + headOffset, x + 25, y - 42 + headOffset); ctx.bezierCurveTo(x + 28, y - 42 + headOffset, x + 28, y - 38 + headOffset, x + 25, y - 35 + headOffset); ctx.fill(); }
  }

  private renderDaye(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x + obj.width / 2, y = obj.y + obj.height;
    const ai = obj.aiState, state = ai?.dayeState || 'sitting_smoke';
    const walkBounce = state === 'walking' && obj.targetX ? Math.abs(Math.sin(this.time * 5)) * 2 : 0;
    const readingOffset = state === 'reading_newspaper' ? 3 : 0;
    ctx.fillStyle = '#2F4F4F'; ctx.fillRect(x - 10, y - 15 + walkBounce, 8, 18); ctx.fillRect(x + 2, y - 15 + walkBounce, 8, 18);
    ctx.fillStyle = '#228B22'; ctx.fillRect(x - 14, y - 45 + walkBounce, 28, 32);
    ctx.fillStyle = PALETTE.skin; ctx.beginPath(); ctx.arc(x, y - 55 + walkBounce - readingOffset, 13, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#DAA520'; ctx.beginPath(); ctx.ellipse(x, y - 62 + walkBounce - readingOffset, 22, 6, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#B8860B'; ctx.beginPath(); ctx.arc(x, y - 64 + walkBounce - readingOffset, 10, 0, Math.PI, true); ctx.fill();
    ctx.fillStyle = '#333'; ctx.beginPath(); ctx.arc(x - 4, y - 55 + walkBounce - readingOffset, 2, 0, Math.PI * 2); ctx.arc(x + 4, y - 55 + walkBounce - readingOffset, 2, 0, Math.PI * 2); ctx.fill();
    if (state === 'sitting_smoke') { const smokeY = Math.sin(this.time * 2) * 5; ctx.fillStyle = 'rgba(200, 200, 200, 0.5)'; ctx.beginPath(); ctx.arc(x + 12, y - 60 + walkBounce + smokeY, 4, 0, Math.PI * 2); ctx.fill(); ctx.beginPath(); ctx.arc(x + 15, y - 68 + walkBounce + smokeY * 1.5, 6, 0, Math.PI * 2); ctx.fill(); }
    if (state === 'reading_newspaper') { ctx.fillStyle = '#F5F5DC'; ctx.fillRect(x - 5, y - 40 + walkBounce, 25, 20); ctx.strokeStyle = '#333'; ctx.lineWidth = 0.5; for (let i = 0; i < 4; i++) { ctx.beginPath(); ctx.moveTo(x - 3, y - 38 + walkBounce + i * 4); ctx.lineTo(x + 17, y - 38 + walkBounce + i * 4); ctx.stroke(); } }
  }

  private renderNeighbor(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x + obj.width / 2, y = obj.y + obj.height;
    const bounce = Math.abs(Math.sin(this.time * 6)) * 3;
    ctx.fillStyle = '#9370DB'; ctx.beginPath(); ctx.ellipse(x, y - 25, 16, 22, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = PALETTE.skin; ctx.beginPath(); ctx.arc(x, y - 50, 13, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#2F1810'; ctx.beginPath(); ctx.arc(x, y - 55, 14, Math.PI, 0); ctx.fill();
    ctx.fillStyle = '#333'; ctx.beginPath(); ctx.arc(x - 4, y - 50, 2, 0, Math.PI * 2); ctx.arc(x + 4, y - 50, 2, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#4B0082'; const legOffset = Math.sin(this.time * 12) * 8; ctx.fillRect(x - 8 + legOffset, y - 12 + bounce, 5, 12); ctx.fillRect(x + 3 - legOffset, y - 12 + bounce, 5, 12);
  }

  private renderChicken(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x + obj.width / 2, y = obj.y + obj.height;
    const hop = Math.abs(Math.sin(this.time * 3 + obj.x)) * 4;
    ctx.fillStyle = '#FFFFFF'; ctx.beginPath(); ctx.ellipse(x, y - 10 + hop, 12, 10, 0, 0, Math.PI * 2); ctx.fill(); ctx.beginPath(); ctx.arc(x + 8, y - 18 + hop, 7, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#FF4500'; ctx.beginPath(); ctx.moveTo(x + 4, y - 23 + hop); ctx.lineTo(x + 8, y - 28 + hop); ctx.lineTo(x + 12, y - 23 + hop); ctx.fill();
    ctx.fillStyle = '#FFA500'; ctx.beginPath(); ctx.moveTo(x + 13, y - 18 + hop); ctx.lineTo(x + 18, y - 15 + hop); ctx.lineTo(x + 13, y - 12 + hop); ctx.fill();
    ctx.fillStyle = '#333'; ctx.beginPath(); ctx.arc(x + 10, y - 19 + hop, 1.5, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#FFA500'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(x - 3, y - 2 + hop); ctx.lineTo(x - 3, y + 5); ctx.moveTo(x + 3, y - 2 + hop); ctx.lineTo(x + 3, y + 5); ctx.stroke();
  }

  private renderCatPet(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x + obj.width / 2, y = obj.y + obj.height;
    const breathe = Math.sin(this.time * 2) * 1;
    const ai = obj.aiState;
    const earTwitch = ai && Math.sin(ai.catEarTimer! * 3) > 0.8 ? 2 : 0;
    ctx.fillStyle = '#FFA500'; ctx.beginPath(); ctx.ellipse(x, y - 8 + breathe, 18, 10, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#FFE4B5'; ctx.beginPath(); ctx.ellipse(x, y - 6 + breathe, 10, 6, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#FFA500'; ctx.beginPath(); ctx.arc(x + 12, y - 12 + breathe, 10, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.moveTo(x + 5, y - 18 + breathe); ctx.lineTo(x + 2 + earTwitch, y - 26 + breathe); ctx.lineTo(x + 9, y - 20 + breathe); ctx.fill();
    ctx.beginPath(); ctx.moveTo(x + 15, y - 20 + breathe); ctx.lineTo(x + 18 - earTwitch, y - 26 + breathe); ctx.lineTo(x + 21, y - 18 + breathe); ctx.fill();
    ctx.fillStyle = '#228B22'; ctx.beginPath(); ctx.arc(x + 10, y - 14 + breathe, 2, 0, Math.PI * 2); ctx.arc(x + 16, y - 14 + breathe, 2, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#FFA500'; ctx.lineWidth = 5; ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(x - 15, y - 10 + breathe); ctx.quadraticCurveTo(x - 25, y - 15 + breathe, x - 22, y - 5 + breathe); ctx.stroke();
    ctx.strokeStyle = '#333'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(x + 18, y - 12 + breathe); ctx.lineTo(x + 25, y - 13 + breathe); ctx.moveTo(x + 18, y - 10 + breathe); ctx.lineTo(x + 25, y - 9 + breathe); ctx.stroke();
  }

  private renderChickenCoop(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x, y = obj.y, w = obj.width, h = obj.height;
    ctx.fillStyle = 'rgba(0,0,0,0.2)'; ctx.fillRect(x + 3, y + h - 5, w, 10);
    ctx.fillStyle = '#DAA520'; ctx.beginPath(); ctx.roundRect(x + 5, y + 10, w - 10, h - 15, 5); ctx.fill();
    ctx.strokeStyle = '#B8860B'; ctx.lineWidth = 1; for (let i = 0; i < 5; i++) { const ly = y + 15 + i * 8; ctx.beginPath(); ctx.moveTo(x + 8, ly); ctx.lineTo(x + w - 8, ly); ctx.stroke(); }
    ctx.fillStyle = '#2F1810'; ctx.beginPath(); ctx.arc(x + w / 2, y + h - 10, 12, 0, Math.PI, true); ctx.fill();
    ctx.fillStyle = '#8B4513'; ctx.beginPath(); ctx.moveTo(x - 5, y + 10); ctx.lineTo(x + w / 2, y - 5); ctx.lineTo(x + w + 5, y + 10); ctx.closePath(); ctx.fill();
    ctx.fillStyle = '#F0E68C'; ctx.beginPath(); ctx.ellipse(x + 10, y + 5, 3, 6, -0.5, 0, Math.PI * 2); ctx.fill(); ctx.beginPath(); ctx.ellipse(x + w - 10, y + 5, 3, 6, 0.5, 0, Math.PI * 2); ctx.fill();
  }

  private renderPigeon(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x + obj.width / 2, y = obj.y + obj.height / 2;
    const wingFlap = Math.sin(this.time * 15) * 5;
    ctx.fillStyle = '#A9A9A9'; ctx.beginPath(); ctx.ellipse(x, y, 10, 6, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#808080'; ctx.beginPath(); ctx.ellipse(x - 3, y - 5 + wingFlap, 12, 4, -0.3, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#A9A9A9'; ctx.beginPath(); ctx.arc(x + 8, y - 2, 5, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#FFA500'; ctx.beginPath(); ctx.moveTo(x + 12, y - 2); ctx.lineTo(x + 15, y); ctx.lineTo(x + 12, y + 1); ctx.fill();
    ctx.fillStyle = '#333'; ctx.beginPath(); ctx.arc(x + 9, y - 3, 1, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#20B2AA'; ctx.beginPath(); ctx.ellipse(x + 4, y, 3, 4, 0, 0, Math.PI * 2); ctx.fill();
  }

  private renderSparrow(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x + obj.width / 2, y = obj.y + obj.height / 2;
    const hop = Math.abs(Math.sin(this.time * 10 + obj.x)) * 3;
    ctx.fillStyle = '#8B7355'; ctx.beginPath(); ctx.ellipse(x, y - hop, 7, 5, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#696969'; ctx.beginPath(); ctx.ellipse(x - 2, y - 3 - hop, 8, 3, -0.2, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#8B7355'; ctx.beginPath(); ctx.arc(x + 5, y - 2 - hop, 4, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#333'; ctx.beginPath(); ctx.moveTo(x + 8, y - 2 - hop); ctx.lineTo(x + 10, y - 1 - hop); ctx.lineTo(x + 8, y - hop); ctx.fill();
    ctx.fillStyle = '#000'; ctx.beginPath(); ctx.arc(x + 6, y - 3 - hop, 1, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#FFA500'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(x - 2, y + 3 - hop); ctx.lineTo(x - 2, y + 6); ctx.moveTo(x + 2, y + 3 - hop); ctx.lineTo(x + 2, y + 6); ctx.stroke();
  }

  private renderButterfly(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x + obj.width / 2, y = obj.y + obj.height / 2;
    const flutter = Math.sin(this.time * 20) * 3;
    const wingColors = ['#FF69B4', '#FFD700', '#9370DB', '#00CED1'];
    const colorIndex = Math.floor(obj.x / 100) % wingColors.length;
    ctx.fillStyle = wingColors[colorIndex]; ctx.beginPath(); ctx.ellipse(x - 3, y - flutter, 5, 8, -0.3, 0, Math.PI * 2); ctx.fill(); ctx.beginPath(); ctx.ellipse(x + 3, y + flutter, 5, 8, 0.3, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#333'; ctx.beginPath(); ctx.ellipse(x, y, 1.5, 6, 0, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#333'; ctx.lineWidth = 0.5; ctx.beginPath(); ctx.moveTo(x, y - 5); ctx.lineTo(x - 3, y - 10); ctx.moveTo(x, y - 5); ctx.lineTo(x + 3, y - 10); ctx.stroke();
  }

  private renderDragonfly(ctx: CanvasRenderingContext2D, obj: GameObject): void {
    const x = obj.x + obj.width / 2, y = obj.y + obj.height / 2;
    const hover = Math.sin(this.time * 8) * 2;
    ctx.fillStyle = '#32CD32'; ctx.beginPath(); ctx.ellipse(x, y + hover, 1.5, 8, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = 'rgba(135, 206, 235, 0.6)'; const wingSpread = Math.abs(Math.sin(this.time * 25)) * 8;
    ctx.beginPath(); ctx.ellipse(x - 3, y - 3 + hover, 3 + wingSpread/3, 8, -0.5, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(x + 3, y - 3 + hover, 3 + wingSpread/3, 8, 0.5, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(x - 3, y + 3 + hover, 2 + wingSpread/4, 6, -0.3, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(x + 3, y + 3 + hover, 2 + wingSpread/4, 6, 0.3, 0, Math.PI * 2); ctx.fill();
  }

  // 装饰物和植物渲染（带风吹效果）
  private renderCoalStove(ctx: CanvasRenderingContext2D, obj: GameObject): void { const x = obj.x, y = obj.y + obj.height, w = obj.width, h = obj.height; ctx.fillStyle = '#4A4A4A'; ctx.beginPath(); ctx.ellipse(x + w/2, y - h/3, w/2, h/4, 0, 0, Math.PI * 2); ctx.fill(); ctx.fillRect(x, y - h/3, w, h/3); ctx.fillStyle = '#2A2A2A'; ctx.beginPath(); ctx.ellipse(x + w/2, y - h/3, w/3, h/6, 0, 0, Math.PI, true); ctx.fill(); ctx.fillStyle = '#FF4500'; ctx.beginPath(); ctx.ellipse(x + w/2, y - h/3 + 2, w/4, h/10, 0, 0, Math.PI, true); ctx.fill(); ctx.fillStyle = '#5A5A5A'; ctx.fillRect(x + w/2 - 5, y - h - 15, 10, 20); ctx.fillStyle = '#4A4A4A'; ctx.fillRect(x + 5, y - 5, 8, 5); ctx.fillRect(x + w - 13, y - 5, 8, 5); }
  private renderManureBasket(ctx: CanvasRenderingContext2D, obj: GameObject): void { const x = obj.x, y = obj.y + obj.height, w = obj.width, h = obj.height; ctx.fillStyle = '#D2B48C'; ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + 5, y - h); ctx.lineTo(x + w - 5, y - h); ctx.lineTo(x + w, y); ctx.closePath(); ctx.fill(); ctx.strokeStyle = '#8B7355'; ctx.lineWidth = 1; for (let i = 0; i < 5; i++) { ctx.beginPath(); ctx.moveTo(x + 5, y - i * (h/5)); ctx.lineTo(x + w - 5, y - i * (h/5)); ctx.stroke(); } ctx.strokeStyle = '#6B4423'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(x + 5, y - h + 5); ctx.quadraticCurveTo(x + w/2, y - h - 10, x + w - 5, y - h + 5); ctx.stroke(); }
  private renderHoe(ctx: CanvasRenderingContext2D, obj: GameObject): void { const x = obj.x, y = obj.y + obj.height, w = obj.width, h = obj.height; ctx.fillStyle = '#8B4513'; ctx.fillRect(x + w/2 - 3, y - h, 6, h - 8); ctx.fillStyle = '#696969'; ctx.beginPath(); ctx.ellipse(x + w/2, y - 8, 12, 8, 0, 0, Math.PI, true); ctx.fill(); ctx.fillStyle = '#4A4A4A'; ctx.beginPath(); ctx.moveTo(x + w/2 - 10, y - 8); ctx.lineTo(x + w/2, y - 3); ctx.lineTo(x + w/2 + 10, y - 8); ctx.fill(); }
  private renderSickle(ctx: CanvasRenderingContext2D, obj: GameObject): void { const x = obj.x + obj.width / 2, y = obj.y + obj.height / 2; ctx.fillStyle = '#C0C0C0'; ctx.beginPath(); ctx.arc(x, y, 10, 0, Math.PI * 1.2); ctx.lineTo(x, y); ctx.closePath(); ctx.fill(); ctx.strokeStyle = '#E0E0E0'; ctx.lineWidth = 1; ctx.beginPath(); ctx.arc(x, y, 7, 0.2, Math.PI); ctx.stroke(); ctx.fillStyle = '#8B4513'; ctx.fillRect(x - 12, y - 2, 15, 4); }
  private renderWinnowingBasket(ctx: CanvasRenderingContext2D, obj: GameObject): void { const x = obj.x, y = obj.y + obj.height, w = obj.width, h = obj.height; ctx.fillStyle = '#D2B48C'; ctx.beginPath(); ctx.moveTo(x, y - h/2); ctx.lineTo(x + w/4, y - h); ctx.lineTo(x + w*3/4, y - h); ctx.lineTo(x + w, y - h/2); ctx.quadraticCurveTo(x + w/2, y, x, y - h/2); ctx.fill(); ctx.strokeStyle = '#8B7355'; ctx.lineWidth = 1; for (let i = 1; i < 4; i++) { ctx.beginPath(); ctx.moveTo(x + i * (w/8), y - h/2 + i * (h/8)); ctx.lineTo(x + w - i * (w/8), y - h/2 + i * (h/8)); ctx.stroke(); } }
  private renderCarryingPole(ctx: CanvasRenderingContext2D, obj: GameObject): void { const x = obj.x, y = obj.y + obj.height / 2, w = obj.width; ctx.fillStyle = '#8B4513'; ctx.fillRect(x, y - 3, w, 6); ctx.strokeStyle = '#654321'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(x + 5, y); ctx.lineTo(x + 5, y + 15); ctx.moveTo(x + w - 5, y); ctx.lineTo(x + w - 5, y + 15); ctx.stroke(); ctx.fillStyle = '#696969'; ctx.beginPath(); ctx.arc(x + 5, y + 18, 3, 0, Math.PI * 2); ctx.arc(x + w - 5, y + 18, 3, 0, Math.PI * 2); ctx.fill(); }

  // 植物渲染带风吹摇摆效果
  private renderSunflower(ctx: CanvasRenderingContext2D, obj: GameObject): void { const x = obj.x + obj.width / 2, y = obj.y + obj.height; const windSway = Math.sin(this.time * 0.5 + obj.x) * 3; ctx.strokeStyle = '#228B22'; ctx.lineWidth = 4; ctx.beginPath(); ctx.moveTo(x, y); ctx.quadraticCurveTo(x + windSway, y - obj.height/2, x + windSway*0.5, y - obj.height + 10); ctx.stroke(); ctx.fillStyle = '#32CD32'; ctx.beginPath(); ctx.ellipse(x - 8, y - obj.height/3, 8, 4, -0.5, 0, Math.PI * 2); ctx.ellipse(x + 8, y - obj.height/2, 8, 4, 0.5, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = '#4A3728'; ctx.beginPath(); ctx.arc(x + windSway*0.5, y - obj.height + 10, 12, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = '#FFD700'; for (let i = 0; i < 12; i++) { const angle = (i / 12) * Math.PI * 2; const px = x + windSway*0.5 + Math.cos(angle) * 15; const py = y - obj.height + 10 + Math.sin(angle) * 15; ctx.beginPath(); ctx.ellipse(px, py, 6, 3, angle, 0, Math.PI * 2); ctx.fill(); } }
  private renderMorningGlory(ctx: CanvasRenderingContext2D, obj: GameObject): void { const x = obj.x + obj.width / 2, y = obj.y + obj.height; const windSway = Math.sin(this.time * 1.5 + obj.x) * 2; ctx.strokeStyle = '#228B22'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(x, y); for (let i = 0; i < 5; i++) ctx.quadraticCurveTo(x + windSway + Math.sin(this.time * 2 + i) * 3, y - (i + 1) * (obj.height/5), x + windSway * 0.5 + Math.sin(this.time + i + 1) * 2, y - (i + 1) * (obj.height/5) - 3); ctx.stroke(); ctx.fillStyle = '#32CD32'; for (let i = 1; i < 4; i++) ctx.beginPath(), ctx.ellipse(x + windSway * 0.3, y - i * (obj.height/4), 6, 10, 0, 0, Math.PI * 2), ctx.fill(); ctx.fillStyle = '#9370DB'; ctx.beginPath(); ctx.arc(x + windSway * 0.5, y - obj.height + 8, 8, 0, Math.PI, true); ctx.fill(); ctx.fillStyle = '#FFFFFF'; ctx.beginPath(); ctx.arc(x + windSway * 0.5, y - obj.height + 8, 3, 0, Math.PI * 2); ctx.fill(); }
  private renderBalsam(ctx: CanvasRenderingContext2D, obj: GameObject): void { const x = obj.x + obj.width / 2, y = obj.y + obj.height; const windSway = Math.sin(this.time * 1.2 + obj.x) * 2; ctx.strokeStyle = '#228B22'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(x, y); ctx.quadraticCurveTo(x + windSway, y - obj.height/2, x + windSway*0.3, y - obj.height + 10); ctx.stroke(); ctx.fillStyle = '#32CD32'; ctx.beginPath(); ctx.ellipse(x - 6 + windSway*0.2, y - obj.height/2, 5, 12, -0.3, 0, Math.PI * 2); ctx.ellipse(x + 6 + windSway*0.2, y - obj.height/2, 5, 12, 0.3, 0, Math.PI * 2); ctx.fill(); const petalColors = ['#FF1493', '#FF69B4', '#FFB6C1']; for (let i = 0; i < 5; i++) { ctx.fillStyle = petalColors[i % 3]; const angle = (i / 5) * Math.PI * 2 - Math.PI/2; const px = x + windSway*0.3 + Math.cos(angle) * 8; const py = y - obj.height + 10 + Math.sin(angle) * 8; ctx.beginPath(); ctx.ellipse(px, py, 5, 8, angle + Math.PI/2, 0, Math.PI * 2); ctx.fill(); } ctx.fillStyle = '#FFD700'; ctx.beginPath(); ctx.arc(x + windSway*0.3, y - obj.height + 10, 3, 0, Math.PI * 2); ctx.fill(); }
  private renderRose(ctx: CanvasRenderingContext2D, obj: GameObject): void { const x = obj.x + obj.width / 2, y = obj.y + obj.height; const windSway = Math.sin(this.time * 0.8 + obj.x) * 2; ctx.strokeStyle = '#228B22'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(x, y); ctx.quadraticCurveTo(x + windSway * 0.5, y - obj.height/2, x + windSway*0.3, y - obj.height + 8); ctx.stroke(); ctx.fillStyle = '#8B4513'; ctx.beginPath(); ctx.moveTo(x, y - obj.height/2); ctx.lineTo(x - 3, y - obj.height/2 - 3); ctx.lineTo(x, y - obj.height/2 - 6); ctx.fill(); ctx.fillStyle = '#32CD32'; ctx.beginPath(); ctx.ellipse(x - 5 + windSway*0.2, y - obj.height/2, 4, 8, -0.5, 0, Math.PI * 2); ctx.ellipse(x + 5 + windSway*0.2, y - obj.height/3, 4, 8, 0.5, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = '#DC143C'; ctx.beginPath(); ctx.arc(x + windSway*0.3, y - obj.height + 8, 8, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = '#B22222'; ctx.beginPath(); ctx.arc(x - 2 + windSway*0.3, y - obj.height + 6, 5, 0, Math.PI * 2); ctx.fill(); }
  private renderPeony(ctx: CanvasRenderingContext2D, obj: GameObject): void { const x = obj.x + obj.width / 2, y = obj.y + obj.height; const windSway = Math.sin(this.time * 0.6 + obj.x) * 2; ctx.strokeStyle = '#228B22'; ctx.lineWidth = 4; ctx.beginPath(); ctx.moveTo(x, y); ctx.quadraticCurveTo(x + windSway, y - obj.height/2, x + windSway*0.3, y - obj.height + 12); ctx.stroke(); ctx.fillStyle = '#228B22'; ctx.beginPath(); ctx.ellipse(x - 8 + windSway*0.2, y - obj.height/2, 8, 12, -0.4, 0, Math.PI * 2); ctx.ellipse(x + 8 + windSway*0.2, y - obj.height/2, 8, 12, 0.4, 0, Math.PI * 2); ctx.fill(); const colors = ['#FFB6C1', '#FFC0CB', '#FF69B4']; for (let layer = 0; layer < 3; layer++) { ctx.fillStyle = colors[layer]; const radius = 14 - layer * 3; const petalCount = 6 + layer * 2; for (let i = 0; i < petalCount; i++) { const angle = (i / petalCount) * Math.PI * 2 + layer * 0.5; const px = x + windSway*0.3 + Math.cos(angle) * (6 - layer); const py = y - obj.height + 12 + Math.sin(angle) * (4 - layer); ctx.beginPath(); ctx.ellipse(px, py, radius/2, radius, angle, 0, Math.PI * 2); ctx.fill(); } } }
  private renderChrysanthemum(ctx: CanvasRenderingContext2D, obj: GameObject): void { const x = obj.x + obj.width / 2, y = obj.y + obj.height; const windSway = Math.sin(this.time * 0.7 + obj.x) * 2; ctx.strokeStyle = '#228B22'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(x, y); ctx.quadraticCurveTo(x + windSway * 0.5, y - obj.height/2, x + windSway*0.3, y - obj.height + 10); ctx.stroke(); ctx.fillStyle = '#32CD32'; ctx.beginPath(); ctx.ellipse(x - 6 + windSway*0.2, y - obj.height/2, 5, 10, -0.3, 0, Math.PI * 2); ctx.ellipse(x + 6 + windSway*0.2, y - obj.height/2, 5, 10, 0.3, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = '#FFD700'; const petalCount = 16; for (let i = 0; i < petalCount; i++) { const angle = (i / petalCount) * Math.PI * 2; const px = x + windSway*0.3 + Math.cos(angle) * 6; const py = y - obj.height + 10 + Math.sin(angle) * 6; ctx.beginPath(); ctx.ellipse(px, py, 3, 12, angle, 0, Math.PI * 2); ctx.fill(); } ctx.fillStyle = '#FFA500'; ctx.beginPath(); ctx.arc(x + windSway*0.3, y - obj.height + 10, 5, 0, Math.PI * 2); ctx.fill(); }

  private renderTargetMarker(ctx: CanvasRenderingContext2D): void {
    const huanhuan = this.objects.find(o => o.type === 'huanhuan');
    if (!huanhuan || !huanhuan.targetX || !huanhuan.targetY) return;
    const pulse = (Math.sin(this.time * 8) + 1) / 2;
    ctx.strokeStyle = `rgba(255, 255, 255, ${0.5 + pulse * 0.5})`; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(huanhuan.targetX, huanhuan.targetY, 10 + pulse * 5, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = `rgba(255, 255, 255, ${0.3 + pulse * 0.3})`; ctx.beginPath(); ctx.arc(huanhuan.targetX, huanhuan.targetY, 5, 0, Math.PI * 2); ctx.fill();
  }
}

// 模块扩展声明
declare module './Scene' {
  interface GameObject {
    _originalX?: number; _originalY?: number; _originalW?: number; _originalH?: number;
  }
}
